import React, { useEffect, useState } from "react";
import { C } from "../constants.js";

export function GenerationHistoryPage({ onMatterClick }) {
  const [history, setHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPageNum] = useState(0);
  const [filters, setFilters] = useState({ matter_id: "", template_name: "", status: "", date_from: "", date_to: "" });
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [retrying, setRetrying] = useState(null);
  const limit = 25;

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", limit);
      params.set("offset", page * limit);
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const resp = await fetch(`/api/generation-history?${params}`);
      if (resp.ok) {
        const data = await resp.json();
        setHistory(data.data || []);
        setTotal(data.total || 0);
      }
    } catch (e) { console.error("History fetch error", e); }
    setLoading(false);
  };

  useEffect(() => { fetchHistory(); }, [page, filters]);

  const viewDetail = async (id) => {
    setDetailLoading(true);
    try {
      const resp = await fetch(`/api/generation-history/${id}`);
      if (resp.ok) setDetail(await resp.json());
    } catch (e) { console.error(e); }
    setDetailLoading(false);
  };

  const retryGen = async (id) => {
    setRetrying(id);
    try {
      const resp = await fetch(`/api/generation-history/${id}/retry`, { method: "POST" });
      if (resp.ok) fetchHistory();
    } catch (e) { console.error(e); }
    setRetrying(null);
  };

  const exportCSV = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    window.open(`/api/generation-history/export/csv?${params}`, "_blank");
  };

  const statusColor = (s) => s === "uploaded" ? "#10B981" : s === "generated" ? "#3B82F6" : "#EF4444";
  const statusLabel = (s) => s === "uploaded" ? "Uploaded" : s === "generated" ? "Generated" : "Failed";
  const totalPages = Math.ceil(total / limit);

  const inputStyle = {
    padding: "8px 12px", borderRadius: "8px", border: `1px solid ${C.warmGray}`,
    fontSize: "13px", background: "#fff", color: C.navy, outline: "none", minWidth: "140px",
  };

  // Detail modal
  const detailModal = detail ? React.createElement("div", {
    style: { position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:5000, display:"flex", alignItems:"center", justifyContent:"center" },
    onClick: () => setDetail(null),
  },
    React.createElement("div", {
      style: { background:"#fff", borderRadius:"16px", padding:"32px", maxWidth:"700px", width:"90%", maxHeight:"80vh", overflowY:"auto" },
      onClick: e => e.stopPropagation(),
    },
      React.createElement("div", { style: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"20px" } },
        React.createElement("h3", { style: { margin:0, color:C.navy, fontSize:"18px" } }, "Generation Details"),
        React.createElement("button", {
          onClick: () => setDetail(null),
          style: { background:"none", border:"none", fontSize:"20px", cursor:"pointer", color:C.textMuted },
        }, "\u2715"),
      ),
      React.createElement("div", { style: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px", marginBottom:"20px" } },
        ...[
          ["Template", detail.template_name],
          ["Matter", detail.matter_display || detail.matter_id],
          ["Status", statusLabel(detail.status)],
          ["Provider", detail.provider_name || "—"],
          ["Generated By", detail.generated_by || "—"],
          ["Date", detail.created_at ? new Date(detail.created_at).toLocaleString() : "—"],
          ["Time", detail.generation_time_ms ? `${detail.generation_time_ms}ms` : "—"],
          ["Clio Doc ID", detail.clio_document_id || "—"],
        ].map(([label, value]) =>
          React.createElement("div", { key: label },
            React.createElement("div", { style: { fontSize:"11px", color:C.textMuted, textTransform:"uppercase", marginBottom:"2px" } }, label),
            React.createElement("div", { style: { fontSize:"14px", color:C.navy, fontWeight:500 } }, value),
          )
        ),
      ),
      detail.error_message ? React.createElement("div", {
        style: { background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:"8px", padding:"12px", marginBottom:"16px" },
      },
        React.createElement("div", { style: { fontSize:"12px", color:"#DC2626", fontWeight:600, marginBottom:"4px" } }, "Error"),
        React.createElement("div", { style: { fontSize:"13px", color:"#7F1D1D" } }, detail.error_message),
      ) : null,
      detail.fields_used ? React.createElement("div", null,
        React.createElement("div", { style: { fontSize:"13px", fontWeight:600, color:C.navy, marginBottom:"8px" } }, "Field Snapshot"),
        React.createElement("div", {
          style: { background:C.cream, borderRadius:"8px", padding:"12px", maxHeight:"200px", overflowY:"auto", fontSize:"12px", fontFamily:"monospace" },
        },
          Object.entries(detail.fields_used).filter(([k]) => !k.startsWith("_")).slice(0, 30).map(([k, v]) =>
            React.createElement("div", { key: k, style: { marginBottom:"4px" } },
              React.createElement("span", { style: { color:C.textMuted } }, k + ": "),
              React.createElement("span", { style: { color:C.navy } }, typeof v === "object" ? JSON.stringify(v) : String(v || "")),
            )
          ),
        ),
      ) : null,
    ),
  ) : null;

  return React.createElement("div", { style: { padding:"32px 40px" } },
    detailModal,
    // Header
    React.createElement("div", { style: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"24px" } },
      React.createElement("div", null,
        React.createElement("h1", { style: { margin:0, color:C.navy, fontSize:"24px", fontWeight:700 } }, "Generation History"),
        React.createElement("p", { style: { margin:"4px 0 0", color:C.textMuted, fontSize:"14px" } }, `${total} records`),
      ),
      React.createElement("button", {
        onClick: exportCSV,
        style: {
          padding:"10px 20px", borderRadius:"8px", border:`1px solid ${C.gold}`,
          background:"transparent", color:C.gold, fontSize:"13px", fontWeight:600, cursor:"pointer",
        },
      }, "\u2B07 Export CSV"),
    ),

    // Filters
    React.createElement("div", {
      style: { display:"flex", gap:"10px", marginBottom:"20px", flexWrap:"wrap", alignItems:"center" },
    },
      React.createElement("input", {
        placeholder: "Matter ID", value: filters.matter_id, style: inputStyle,
        onChange: e => { setFilters(f => ({ ...f, matter_id: e.target.value })); setPageNum(0); },
      }),
      React.createElement("select", {
        value: filters.status, style: inputStyle,
        onChange: e => { setFilters(f => ({ ...f, status: e.target.value })); setPageNum(0); },
      },
        React.createElement("option", { value: "" }, "All Statuses"),
        React.createElement("option", { value: "uploaded" }, "Uploaded"),
        React.createElement("option", { value: "generated" }, "Generated"),
        React.createElement("option", { value: "failed" }, "Failed"),
      ),
      React.createElement("input", {
        type: "date", value: filters.date_from, style: inputStyle,
        onChange: e => { setFilters(f => ({ ...f, date_from: e.target.value })); setPageNum(0); },
      }),
      React.createElement("input", {
        type: "date", value: filters.date_to, style: inputStyle,
        onChange: e => { setFilters(f => ({ ...f, date_to: e.target.value })); setPageNum(0); },
      }),
      (filters.matter_id || filters.status || filters.date_from || filters.date_to) ?
        React.createElement("button", {
          onClick: () => { setFilters({ matter_id:"", template_name:"", status:"", date_from:"", date_to:"" }); setPageNum(0); },
          style: { background:"none", border:"none", color:C.gold, cursor:"pointer", fontSize:"13px", fontWeight:500 },
        }, "Clear filters") : null,
    ),

    // Table
    loading ? React.createElement("div", { style: { textAlign:"center", padding:"40px", color:C.textMuted } }, "Loading...") :
    history.length === 0 ? React.createElement("div", { style: { textAlign:"center", padding:"40px", color:C.textMuted } }, "No generation records found.") :
    React.createElement("div", { style: { background:"#fff", borderRadius:"12px", boxShadow:"0 1px 3px rgba(0,0,0,0.08)", overflow:"hidden" } },
      React.createElement("table", { style: { width:"100%", borderCollapse:"collapse", fontSize:"13px" } },
        React.createElement("thead", null,
          React.createElement("tr", { style: { background:C.cream, textAlign:"left" } },
            ...["Date", "Matter", "Template", "Provider", "Status", "Time", "Actions"].map(h =>
              React.createElement("th", { key: h, style: { padding:"12px 16px", color:C.textMuted, fontWeight:600, fontSize:"11px", textTransform:"uppercase", borderBottom:`1px solid ${C.warmGray}` } }, h)
            ),
          ),
        ),
        React.createElement("tbody", null,
          history.map(row =>
            React.createElement("tr", { key: row.id, style: { borderBottom:`1px solid ${C.warmGray}40` } },
              React.createElement("td", { style: { padding:"12px 16px", color:C.navy } }, row.created_at ? new Date(row.created_at).toLocaleDateString() : "—"),
              React.createElement("td", { style: { padding:"12px 16px" } },
                React.createElement("span", {
                  style: { color:C.gold, cursor:"pointer", fontWeight:500 },
                  onClick: () => onMatterClick && onMatterClick({ id: Number(row.matter_id), display_number: row.matter_display }),
                }, row.matter_display || row.matter_id),
              ),
              React.createElement("td", { style: { padding:"12px 16px", color:C.navy, maxWidth:"200px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" } }, row.template_name),
              React.createElement("td", { style: { padding:"12px 16px", color:C.textMuted } }, row.provider_name || "—"),
              React.createElement("td", { style: { padding:"12px 16px" } },
                React.createElement("span", {
                  style: { display:"inline-block", padding:"3px 10px", borderRadius:"12px", fontSize:"11px", fontWeight:600, background:statusColor(row.status) + "18", color:statusColor(row.status) },
                }, statusLabel(row.status)),
              ),
              React.createElement("td", { style: { padding:"12px 16px", color:C.textMuted } }, row.generation_time_ms ? `${row.generation_time_ms}ms` : "—"),
              React.createElement("td", { style: { padding:"12px 16px", display:"flex", gap:"6px" } },
                React.createElement("button", {
                  onClick: () => viewDetail(row.id),
                  style: { background:"none", border:"none", color:C.gold, cursor:"pointer", fontSize:"12px", fontWeight:500 },
                }, "View"),
                row.status === "failed" ? React.createElement("button", {
                  onClick: () => retryGen(row.id),
                  disabled: retrying === row.id,
                  style: { background:"none", border:"none", color:"#EF4444", cursor:"pointer", fontSize:"12px", fontWeight:500 },
                }, retrying === row.id ? "..." : "Retry") : null,
              ),
            ),
          ),
        ),
      ),
    ),

    // Pagination
    totalPages > 1 ? React.createElement("div", {
      style: { display:"flex", justifyContent:"center", gap:"8px", marginTop:"20px", alignItems:"center" },
    },
      React.createElement("button", {
        onClick: () => setPageNum(p => Math.max(0, p - 1)), disabled: page === 0,
        style: { padding:"8px 16px", borderRadius:"8px", border:`1px solid ${C.warmGray}`, background:"#fff", cursor: page > 0 ? "pointer" : "default", color: page > 0 ? C.navy : C.textMuted },
      }, "\u2190 Prev"),
      React.createElement("span", { style: { fontSize:"13px", color:C.textMuted } }, `Page ${page + 1} of ${totalPages}`),
      React.createElement("button", {
        onClick: () => setPageNum(p => Math.min(totalPages - 1, p + 1)), disabled: page >= totalPages - 1,
        style: { padding:"8px 16px", borderRadius:"8px", border:`1px solid ${C.warmGray}`, background:"#fff", cursor: page < totalPages - 1 ? "pointer" : "default", color: page < totalPages - 1 ? C.navy : C.textMuted },
      }, "Next \u2192"),
    ) : null,
  );
}


